// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Work orders screen (spec 005 — external provider oversight).
 *
 * Read-only. Work is executed by an outside provider and the records live in
 * an external WOMS; this screen is the operational view of what was routed
 * out, who owns it, and how it ended. Creating and updating orders is a
 * separate slice.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, ComboBox, Search, Select, SelectItem, TextInput } from '@carbon/react';
import { useWorkOrderLookup, useWorkOrders, useWorkOrderSummary } from '../../hooks/useWorkOrders';
import { useWorkOrderAssign, useWorkOrderStatus } from '../../hooks/useWorkOrderMutation';
import type { BusinessOwner, WorkOrder, WorkOrderPriority, WorkOrderStatus } from '../../types';
import { PageHead, EmptyState, Loading } from '../../components/shell/ScreenHelpers';
import WorkOrderCreateModal, { ownerLabel, searchFilter } from './WorkOrderCreateModal';
import { useSiteTime } from '../../hooks/useSiteTime';
import { formatCalendarDate } from '../../utils/siteTime';
import { useUrlState } from '../../hooks/useUrlState';
import { useAlarmLinkOf, type AlarmLink } from '../../hooks/useAlarmWorkOrderLinks';
import { useActiveSite } from '../../hooks/useActiveSite';
import {
  UNKNOWN_OWNER,
  useBusinessOwnerName,
  useBusinessOwners,
} from '../../hooks/useWorkOrderDirectory';
import { useAlarm } from '../../hooks/useAlarms';
import { zonedDayKey } from '../../utils/siteTime';
import { StaleDataNotice } from '../../components/common/StaleDataNotice';
import { useIsMounted } from '../../hooks/useIsMounted';
import './WorkOrders.css';

const STATUS_CHOICES: { value: WorkOrderStatus; label: string }[] = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'canceled', label: 'Canceled' },
];

const STATUS_LABEL = Object.fromEntries(STATUS_CHOICES.map(c => [c.value, c.label])) as Record<
  WorkOrderStatus,
  string
>;

const STATUS_FILTERS: { key: string; label: string; statuses?: WorkOrderStatus[] }[] = [
  { key: 'open', label: 'Open', statuses: ['scheduled', 'in_progress', 'on_hold'] },
  { key: 'scheduled', label: 'Scheduled', statuses: ['scheduled'] },
  { key: 'in_progress', label: 'In progress', statuses: ['in_progress'] },
  { key: 'completed', label: 'Completed', statuses: ['completed'] },
  { key: 'all', label: 'All' },
];

const SORTS = [
  { key: 'newest', label: 'Newest' },
  { key: 'updated', label: 'Updated' },
  { key: 'due', label: 'Due' },
  { key: 'priority', label: 'Priority' },
  { key: 'overdue', label: 'Overdue' },
];

const PRIORITY_FILTERS: WorkOrderPriority[] = ['HIGH', 'MEDIUM', 'LOW', 'NONE'];

const PRIORITY_RANK: Record<string, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 };
const priorityRank = (order: WorkOrder) => PRIORITY_RANK[order.priority?.toUpperCase() ?? ''] ?? 3;
const createdAt = (order: Pick<WorkOrder, 'created_at'>) =>
  order.created_at ? Date.parse(order.created_at) : Number.NaN;
const newestFirst = (a: WorkOrder, b: WorkOrder) => {
  const [x, y] = [createdAt(a), createdAt(b)];
  if (Number.isNaN(x) || Number.isNaN(y)) return Number(Number.isNaN(x)) - Number(Number.isNaN(y));
  return y - x;
};

/** Most recent first; undated last. Null-safe like newestFirst. */
const latestOf = (field: 'updated_at' | 'expected_completion') => (a: WorkOrder, b: WorkOrder) => {
  const [x, y] = [a[field], b[field]].map(v => (v ? Date.parse(v) : Number.NaN));
  if (Number.isNaN(x) || Number.isNaN(y)) return Number(Number.isNaN(x)) - Number(Number.isNaN(y));
  return y - x;
};

const SORTERS: Record<string, (a: WorkOrder, b: WorkOrder) => number> = {
  newest: newestFirst,
  updated: (a, b) => latestOf('updated_at')(a, b) || newestFirst(a, b),
  // Soonest due first; undated last, as in latestOf.
  due: (a, b) => {
    const [x, y] = [a, b].map(o =>
      o.expected_completion ? Date.parse(o.expected_completion) : Number.NaN
    );
    if (Number.isNaN(x) || Number.isNaN(y)) {
      return Number(Number.isNaN(x)) - Number(Number.isNaN(y)) || newestFirst(a, b);
    }
    return x - y || newestFirst(a, b);
  },
  priority: (a, b) => priorityRank(a) - priorityRank(b) || newestFirst(a, b),
  overdue: (a, b) => Number(b.update_overdue) - Number(a.update_overdue) || newestFirst(a, b),
};

// The extra_fields label the WOMS stores the raising alarm's id under (the BFF
// derives source_alarm from it); shown as "Alarm ID", never as the raw key.
const ALARM_ID_FIELD = 'energyfluo_alarm_id';
const EXTRA_FIELD_LABEL: Record<string, string> = { [ALARM_ID_FIELD]: 'Alarm ID' };
/** A readable label for a caller metadata key: mapped, else "some_key" → "Some key". */
const extraFieldLabel = (key: string) =>
  EXTRA_FIELD_LABEL[key] ?? key.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

/** Initials for the owner avatar: "R. Alvarez" -> "RA". */
const initials = (name: string) =>
  name
    .split(/[\s.]+/)
    .filter(Boolean)
    .map(part => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

/**
 * "October 2026" in the site's zone, like every other date on this screen:
 * near a month boundary the viewer's own clock can be in a different month.
 */
export const monthLabel = (timeZone: string, now: Date = new Date()) =>
  now.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone });

/**
 * Whether `order` was created inside the `from`..`to` day range (YYYY-MM-DD),
 * with days in the site's zone: the same clock the rows show Sent in.
 */
export function createdWithin(
  order: Pick<WorkOrder, 'created_at'>,
  from: string,
  to: string,
  timeZone: string
) {
  if (!from && !to) return true;
  const at = createdAt(order);
  if (Number.isNaN(at)) return false;
  const day = zonedDayKey(new Date(at), timeZone);
  return (!from || day >= from) && (!to || day <= to);
}

/**
 * The alarm behind a link, read from AEM: when it was raised, how severe, and
 * where. The link's path carries the lookup (see alarmDetailPath), so a link
 * this browser remembered resolves as well as the BFF's.
 */
function LinkedAlarmCard({ link, format }: { link: AlarmLink; format: SiteTimeFormat }) {
  const url = new URL(link.path, 'http://helios.local');
  const code = decodeURIComponent(url.pathname.split('/').pop() ?? '');
  const params = url.searchParams;
  const { alarm } = useAlarm(
    code || undefined,
    params.get('site') ?? undefined,
    params.get('equipment') ?? undefined,
    params.get('alarm_id') ?? undefined
  );
  return (
    <div className="wo-alarm-card">
      <code>{link.alarmId}</code>
      {(link.label || alarm?.summary) && <strong>{link.label || alarm?.summary}</strong>}
      {alarm && (
        <small data-testid="linked-alarm-facts">
          {alarm.sev} · {alarm.eventTimestamp ? format(alarm.eventTimestamp, 'dateTime') : '—'} ·{' '}
          {alarm.asset}
          {alarm.location && ` · ${alarm.location}`}
        </small>
      )}
      <Link to={link.path}>Open alarm →</Link>
    </div>
  );
}

type SiteTimeFormat = ReturnType<typeof useSiteTime>['format'];

function OverdueChip() {
  return (
    <span className="wo-chip wo-chip--overdue">
      <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" aria-hidden>
        <circle cx="8" cy="8" r="6" strokeWidth="1.5" />
        <path d="M8 4.8V8l2.2 1.6" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      Update overdue
    </span>
  );
}

export default function WorkOrders() {
  const isMounted = useIsMounted();
  const { format, timeZone } = useSiteTime();
  // Work orders belong to a site. In fleet mode (managers) there is no site to
  // name, and only the deprecated unscoped routes list across every site.
  const { siteId, siteLabel, isFleet, options, setSite } = useActiveSite();
  const scope = isFleet ? null : siteId;
  const [urlState, patchUrl] = useUrlState({
    status: 'open',
    q: '',
    wo: '',
    sort: 'newest',
    site: '',
    pri: '',
    owner: '',
    from: '',
    to: '',
    alarm: '',
  });

  // `?site=` (workOrderPath) names the linked order's site, which can differ
  // from the active one. Switch to it once the roster has loaded, so the header
  // and the list agree, then drop it. A site the user doesn't hold is dropped
  // without switching, and the order then reads as not found.
  const linkedSite = urlState.site;
  useEffect(() => {
    if (!linkedSite || options.length === 0) return;
    if (options.some(o => o.siteId === linkedSite) && (isFleet || siteId !== linkedSite)) {
      setSite(linkedSite);
    }
    patchUrl({ site: '' });
  }, [linkedSite, options, isFleet, siteId, setSite, patchUrl]);

  // No site resolved yet (roster loading, failed or empty), or a linked site
  // still to switch to: read nothing rather than fall through to the
  // fleet-wide routes, which a site-scoped operator would otherwise pass.
  const hasScope = !linkedSite && (isFleet || !!siteId);
  // Work orders belong to a site, so the fleet view can't create one.
  const canCreate = hasScope && !isFleet;
  const filter = STATUS_FILTERS.some(f => f.key === urlState.status) ? urlState.status : 'open';
  const sort = SORTS.some(s => s.key === urlState.sort) ? urlState.sort : 'newest';
  const selectedId = urlState.wo || null;
  const [createOpen, setCreateOpen] = useState(false);
  const statusMutation = useWorkOrderStatus();
  const assignMutation = useWorkOrderAssign();
  const { owners } = useBusinessOwners();

  const statuses = useMemo(() => STATUS_FILTERS.find(f => f.key === filter)?.statuses, [filter]);
  const {
    workOrders,
    loading,
    isFetching,
    error,
    refreshError,
    refetch,
    hasNext,
    loadMore,
    loadingMore,
    loadMoreError,
  } = useWorkOrders(
    scope,
    {
      status: statuses,
      page_size: 50,
    },
    hasScope
  );
  const {
    summary,
    refreshError: summaryRefreshError,
    refetch: refetchSummary,
  } = useWorkOrderSummary(scope, hasScope);
  // business_owner is the owner's upstream id; the directory names it.
  const ownerName = useBusinessOwnerName();

  const alarmLinkOf = useAlarmLinkOf();

  // ponytail: client-side over the loaded pages; move to the upstream `title`
  // param (title only) or a BFF search if loading pages to search gets tedious.
  const keyword = urlState.q.trim().toLowerCase();
  const { pri, owner, from, to } = urlState;
  const alarmFilter = urlState.alarm.trim().toLowerCase();
  const visible = useMemo(() => {
    const refined = workOrders.filter(order => {
      if (pri && (order.priority ?? 'NONE') !== pri) return false;
      if (owner && order.business_owner !== owner) return false;
      if (!createdWithin(order, from, to, timeZone)) return false;
      if (alarmFilter && !alarmLinkOf(order)?.alarmId.toLowerCase().includes(alarmFilter))
        return false;
      return true;
    });
    const matching = !keyword
      ? refined
      : refined.filter(order => {
          const alarm = alarmLinkOf(order);
          return [
            order.reference,
            order.title,
            order.provider,
            ownerName(order.business_owner),
            order.status_label,
            alarm?.label,
            alarm?.alarmId,
          ].some(field => field?.toLowerCase().includes(keyword));
        });
    return [...matching].sort(SORTERS[sort]);
  }, [
    workOrders,
    alarmLinkOf,
    ownerName,
    keyword,
    sort,
    pri,
    owner,
    from,
    to,
    alarmFilter,
    timeZone,
  ]);
  const refining = !!(pri || owner || from || to || alarmFilter);

  const listed = workOrders.find(order => order.id === selectedId);
  const { workOrder: linked, unavailable } = useWorkOrderLookup(
    scope,
    selectedId,
    hasScope && !loading && !listed && !keyword && !refining
  );
  // A narrowed list (keyword or filters) owns the selection: an order it hides
  // is not shown in the detail either.
  const selected =
    keyword || refining
      ? (visible.find(order => order.id === selectedId) ?? visible[0] ?? null)
      : selectedId
        ? (listed ?? linked)
        : (visible[0] ?? null);
  // List rows never carry originating_task (upstream returns it only from the
  // single read), so a listed order is read once by id for that field.
  const { workOrder: fetched } = useWorkOrderLookup(
    scope,
    selected?.id ?? null,
    hasScope && !loading && !!selected
  );
  const originatingTask = fetched?.originating_task ?? selected?.originating_task;
  const raisingAlarm = selected ? alarmLinkOf(selected) : undefined;

  const [pick, setPick] = useState<{ id: string; status: string } | null>(null);
  const pendingStatus = pick && pick.id === selected?.id ? pick.status : '';
  const setPendingStatus = (status: string) =>
    setPick(selected && status ? { id: selected.id, status } : null);
  const [ownerPick, setOwnerPick] = useState<{ id: string; owner: string } | null>(null);
  // A refused reassign is about one order and one owner: clear it when either changes.
  const { reset: resetAssign } = assignMutation;
  useEffect(() => resetAssign(), [selected?.id, resetAssign]);
  const pendingOwner = ownerPick && ownerPick.id === selected?.id ? ownerPick.owner : '';
  const pendingOwnerItem = owners.find(o => o.id === pendingOwner) ?? null;

  const detail: [string, React.ReactNode][] = selected
    ? [
        ['Sent', selected.created_at ? format(selected.created_at, 'dateTime') : '—'],
        ['Last updated', selected.updated_at ? format(selected.updated_at, 'dateTime') : '—'],
        ['Originating task', originatingTask ?? '—'],
        ['External provider', selected.provider ?? 'Unassigned'],
        ['Expected completion', formatCalendarDate(selected.expected_completion)],
        ['Priority', selected.priority ?? '—'],
        ['Outcome', selected.outcome ?? 'Not yet recorded'],
        // Caller metadata the WOMS stores against the order, e.g. the alarm id.
        ...Object.entries(selected.extra_fields ?? {}).map(
          ([key, value]): [string, React.ReactNode] => [
            extraFieldLabel(key),
            key === ALARM_ID_FIELD && raisingAlarm ? (
              <Link to={raisingAlarm.path}>{value}</Link>
            ) : (
              value
            ),
          ]
        ),
      ]
    : [];
  const selectedOwner = selected ? ownerName(selected.business_owner) : null;
  const listFooter = hasNext ? (
    <div className="wo-more">
      {loadMoreError && (
        <p className="wo-more-error" role="alert">
          Could not load more work orders. {loadMoreError}
        </p>
      )}
      <Button
        kind="tertiary"
        size="sm"
        onClick={loadMore}
        disabled={loadingMore || isFetching}
        aria-busy={loadingMore}
      >
        {loadingMore ? 'Loading more…' : loadMoreError ? 'Try again' : 'Load more'}
      </Button>
      <span className="wo-more-note">
        {workOrders.length} loaded{keyword ? ' · search covers loaded orders only' : ''}
      </span>
    </div>
  ) : (
    workOrders.length > 0 && (
      <p className="wo-more wo-more-end">All {workOrders.length} work orders loaded</p>
    )
  );
  const monthName = monthLabel(timeZone);

  return (
    <div className="hs-page">
      <PageHead
        title="Work orders"
        dateline={`${isFleet ? 'Whole fleet' : siteLabel} · provider status, ownership and outcome`}
        actions={
          <>
            <div className="wo-sort" role="group" aria-label="Sort work orders">
              {SORTS.map(s => (
                <button
                  key={s.key}
                  type="button"
                  className={sort === s.key ? 'active' : undefined}
                  aria-pressed={sort === s.key}
                  onClick={() => patchUrl({ sort: s.key })}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {isFleet && <span className="wo-create-note">Pick a site to route work</span>}
            <Button size="sm" disabled={!canCreate} onClick={() => setCreateOpen(true)}>
              Route approved work
            </Button>
          </>
        }
      />

      {/* Mounted only while open, as in AlarmNextStepCard: a closed Carbon modal
          still renders its fields, whose controls would sit in the page. */}
      {createOpen && (
        <WorkOrderCreateModal
          open
          siteId={scope}
          onClose={() => setCreateOpen(false)}
          onCreated={workOrder => {
            // Runs from the hook's own onSuccess, so a slow create still
            // fires after the operator has navigated elsewhere — but these
            // are presentation only (closing this modal, patching THIS
            // page's URL), nothing durable like an alarm link, so skip them
            // once this page is gone rather than rewriting a stale history.
            if (!isMounted.current) return;
            setCreateOpen(false);
            patchUrl({ wo: workOrder.id });
            setPendingStatus('');
          }}
        />
      )}

      {(refreshError || summaryRefreshError) && (
        <StaleDataNotice
          what={refreshError ? 'work orders' : 'work order counts'}
          error={refreshError ?? summaryRefreshError}
          onRetry={() => {
            refetch();
            refetchSummary();
          }}
        />
      )}

      {summary && (
        <section className="wo-kpis" aria-label="Work order summary">
          <article>
            <span>Open work orders</span>
            <strong>{summary.open_count}</strong>
            <small>Not yet completed</small>
          </article>
          <article>
            <span>Awaiting provider update</span>
            <strong className={summary.awaiting_update_count > 0 ? 'wo-risk' : undefined}>
              {summary.awaiting_update_count}
            </strong>
            <small>Past expected update window</small>
          </article>
          <article>
            <span>Completed this month</span>
            <strong className="wo-done">{summary.completed_this_month}</strong>
            <small>{monthName}</small>
          </article>
          {summary.counts_capped && (
            <p className="wo-capped">
              Counts are a lower bound — the provider system reports no totals, so these stop at a
              page limit.
            </p>
          )}
        </section>
      )}

      <div className="wo-search">
        <Search
          size="lg"
          labelText="Search work orders"
          placeholder="Search by work order, title, provider, owner, device or alarm…"
          value={urlState.q}
          onChange={e => patchUrl({ q: e.target.value })}
        />
      </div>

      <div className="hs-toolbar wo-filters">
        {STATUS_FILTERS.map(f => (
          <button
            key={f.key}
            type="button"
            className={`wo-filter wo-filter--${f.key}${filter === f.key ? ' active' : ''}`}
            aria-pressed={filter === f.key}
            // A new filter is a new list: drop the selection, or the by-id lookup
            // keeps showing an order the list no longer holds.
            onClick={() => patchUrl({ status: f.key, wo: null })}
          >
            <span className="wo-filter-dot" aria-hidden />
            {f.label}
            {filter === f.key && !loading && (
              <span className="wo-filter-count">
                {/* Leading space: read as "Open 3", trimmed visually. */} {visible.length}
                {hasNext && '+'}
              </span>
            )}
          </button>
        ))}
        {isFetching && !loading && <span className="wo-updating">Updating…</span>}
      </div>

      {/* ponytail: client-side over the loaded pages, like search; the WOMS
          filters only by status, title, provider and updated_since. */}
      <div className="wo-refine" role="group" aria-label="Refine work orders">
        <Select
          id="wo-filter-priority"
          labelText="Priority"
          size="sm"
          value={pri}
          onChange={e => patchUrl({ pri: e.target.value })}
        >
          <SelectItem value="" text="Any" />
          {PRIORITY_FILTERS.map(p => (
            <SelectItem key={p} value={p} text={p} />
          ))}
        </Select>
        <Select
          id="wo-filter-owner"
          labelText="Assignee"
          size="sm"
          value={owner}
          onChange={e => patchUrl({ owner: e.target.value })}
        >
          <SelectItem value="" text="Anyone" />
          {owners.map(o => (
            <SelectItem key={o.id} value={o.id} text={o.name} />
          ))}
        </Select>
        <TextInput
          id="wo-filter-from"
          labelText="Sent from"
          type="date"
          size="sm"
          value={from}
          onChange={e => patchUrl({ from: e.target.value })}
        />
        <TextInput
          id="wo-filter-to"
          labelText="Sent to"
          type="date"
          size="sm"
          value={to}
          onChange={e => patchUrl({ to: e.target.value })}
        />
        <TextInput
          id="wo-filter-alarm"
          labelText="Alarm ID"
          size="sm"
          value={urlState.alarm}
          onChange={e => patchUrl({ alarm: e.target.value })}
        />
        {refining && (
          <Button
            kind="ghost"
            size="sm"
            onClick={() => patchUrl({ pri: '', owner: '', from: '', to: '', alarm: '' })}
          >
            Clear filters
          </Button>
        )}
      </div>

      {!hasScope && (
        <EmptyState
          big="No site selected"
          sub="Work orders belong to a site. Pick one of your sites to see its work orders."
        />
      )}

      {loading && <Loading label="Loading work orders…" />}

      {!loading && error && (
        <div className="wo-error" role="alert">
          <p>Could not load work orders. {error}</p>
          <button type="button" className="hs-pill" onClick={refetch}>
            Try again
          </button>
        </div>
      )}

      {hasScope && !loading && !error && workOrders.length === 0 && (
        <EmptyState
          big="No work orders"
          sub="Nothing has been routed to a provider for this filter."
        />
      )}

      {!loading && !error && workOrders.length > 0 && visible.length === 0 && (
        <>
          <EmptyState
            big="No matching work orders"
            sub={
              keyword
                ? `Nothing ${hasNext ? 'loaded so far ' : ''}matches “${urlState.q}”.`
                : `Nothing ${hasNext ? 'loaded so far ' : ''}matches these filters.`
            }
          />
          {listFooter}
        </>
      )}

      {!loading && !error && visible.length > 0 && (
        <div className="wo-grid">
          <section className="wo-list" aria-label="Active work orders">
            {visible.map(order => {
              const rowAlarm = alarmLinkOf(order);
              const priority = order.priority?.toUpperCase();
              return (
                <div
                  key={order.id}
                  className={`wo-row wo-pri--${priority?.toLowerCase() ?? 'none'}${selected?.id === order.id ? ' selected' : ''}`}
                  data-testid="work-order-row"
                  data-work-order-id={order.id}
                >
                  <button
                    type="button"
                    className="wo-row-btn"
                    onClick={() => {
                      patchUrl({ wo: order.id }, { push: true });
                      setPendingStatus('');
                    }}
                  >
                    <span className="wo-row-tags">
                      {priority && priority !== 'NONE' && (
                        <span className="wo-chip wo-chip--priority">{priority}</span>
                      )}
                      <span className={`wo-chip wo-status--${order.status}`}>
                        {STATUS_LABEL[order.status] ?? order.status_label}
                      </span>
                      {order.update_overdue && <OverdueChip />}
                    </span>
                    <span className="wo-row-title">
                      <code>{order.reference}</code>
                      <strong>{order.title}</strong>
                    </span>
                    <small>
                      Sent {order.created_at ? format(order.created_at, 'dateTimeShort') : '—'} ·
                      Updated {order.updated_at ? format(order.updated_at, 'dateTimeShort') : '—'} ·
                      Provider: {order.provider ?? 'Unassigned'} · Owner:{' '}
                      {ownerName(order.business_owner) ?? 'Unassigned'} · Due{' '}
                      {formatCalendarDate(order.expected_completion)}
                    </small>
                  </button>
                  {/* Outside the row button: a link can't nest in a button. */}
                  {rowAlarm && (
                    <Link className="wo-row-alarm" to={rowAlarm.path}>
                      Alarm {rowAlarm.alarmId}
                      {rowAlarm.label && ` · ${rowAlarm.label}`} →
                    </Link>
                  )}
                </div>
              );
            })}
            {listFooter}
          </section>

          {!selected && selectedId && unavailable && (
            <aside className="wo-detail" aria-label="Work order not found">
              <EmptyState
                big={`Work order ${selectedId} not found`}
                sub="It may have been deleted, the link may be out of date, or it couldn't be loaded. Pick an order from the list."
              />
            </aside>
          )}

          {selected && (
            <aside
              className="wo-detail"
              aria-label={`Work order ${selected.reference}`}
              data-testid="work-order-detail"
            >
              <header className="wo-detail-head">
                <div>
                  <code>{selected.reference}</code>
                  <h2>{selected.title}</h2>
                </div>
                <span className={`wo-chip wo-status--${selected.status}`}>
                  {STATUS_LABEL[selected.status] ?? selected.status_label}
                </span>
              </header>
              {selected.update_overdue && <OverdueChip />}

              {raisingAlarm && (
                <section className="wo-section" aria-label="Originating alarm">
                  <h3>Originating alarm</h3>
                  <LinkedAlarmCard link={raisingAlarm} format={format} />
                </section>
              )}

              <section className="wo-section" aria-label="Details">
                <h3>Details</h3>
                <dl className="wo-details">
                  {detail.map(([label, value]) => (
                    <React.Fragment key={label}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </section>

              <section className="wo-section" aria-label="Owner">
                <h3>Owner</h3>
                <div className="wo-owner">
                  <span className="wo-avatar" aria-hidden>
                    {selectedOwner && selectedOwner !== UNKNOWN_OWNER
                      ? initials(selectedOwner)
                      : '—'}
                  </span>
                  {selectedOwner ?? 'Unassigned'}
                </div>
                <div className="wo-assign">
                  <ComboBox<BusinessOwner>
                    id="wo-assign-owner"
                    titleText="Reassign to"
                    placeholder="Search by name or email"
                    size="sm"
                    items={owners}
                    itemToString={ownerLabel}
                    shouldFilterItem={searchFilter<BusinessOwner>(ownerLabel(pendingOwnerItem))}
                    selectedItem={pendingOwnerItem}
                    onChange={({ selectedItem }) => {
                      resetAssign();
                      setOwnerPick(
                        selectedItem ? { id: selected.id, owner: selectedItem.id } : null
                      );
                    }}
                    disabled={assignMutation.isPending}
                  />
                  <Button
                    size="sm"
                    kind="tertiary"
                    disabled={
                      assignMutation.isPending ||
                      !pendingOwner ||
                      pendingOwner === selected.business_owner
                    }
                    onClick={() =>
                      assignMutation.mutate(
                        { siteId: scope, id: selected.id, business_owner: pendingOwner },
                        { onSuccess: () => setOwnerPick(null) }
                      )
                    }
                  >
                    {assignMutation.isPending ? 'Assigning…' : 'Assign'}
                  </Button>
                  {assignMutation.isError && (
                    <p className="wo-status-error" role="alert">
                      {assignMutation.error.message}
                    </p>
                  )}
                </div>
              </section>

              <div className="wo-status-change">
                <Select
                  id="wo-status-change"
                  labelText="Change status"
                  value={pendingStatus || selected.status}
                  onChange={e => setPendingStatus(e.target.value)}
                  disabled={statusMutation.isPending}
                >
                  {STATUS_CHOICES.map(choice => (
                    <SelectItem key={choice.value} value={choice.value} text={choice.label} />
                  ))}
                </Select>
                <Button
                  size="sm"
                  disabled={
                    statusMutation.isPending || !pendingStatus || pendingStatus === selected.status
                  }
                  onClick={() =>
                    statusMutation.mutate(
                      { siteId: scope, id: selected.id, status: pendingStatus as WorkOrderStatus },
                      { onSuccess: () => setPendingStatus('') }
                    )
                  }
                >
                  {statusMutation.isPending ? 'Updating…' : 'Update status'}
                </Button>
                <p className="wo-status-note">
                  Applied directly in the provider&apos;s system. A change made there in the
                  meantime is overwritten without warning.
                </p>
                {statusMutation.isError && (
                  <p className="wo-status-error" role="alert">
                    {statusMutation.error.message}
                  </p>
                )}
              </div>
            </aside>
          )}
        </div>
      )}
    </div>
  );
}
